import type { CampaignPhase, CampaignPlacement, StoreImage, StoreProfileDetail } from '../types';
import type { SpatialEntity } from './spatialEntity';
import { buildSpatialEntities, resolveCampaignSpatialEntity } from './spatialEntity';
import { deriveCampaignPhase, phaseLabel, resolveCampaignPhaseAtDate, campaignPhaseInputFromPlacement } from './campaignPhase';
import { resolveEvidenceImageUrl } from './evidenceImageUrl';
import { getInstallImagesForInstoreLocation } from './instoreLocationGroups';

export type CampaignBrowseFilter = 'recent' | 'active' | 'previous' | 'planned' | 'all';

export interface CampaignCategoryGroup {
  key: string;
  locationName: string;
  instoreLocationId: number | null;
  floorplanZoneId: string | null;
  onMap: boolean;
  campaigns: CampaignPlacement[];
  previewImageUrl: string | null;
}

export interface EnrichedCampaign extends CampaignPlacement {
  evidenceImages: StoreImage[];
  primaryImageUrl: string | null;
  locationLabel: string;
  onMap: boolean;
  mapFocus: { kind: SpatialEntity['kind']; id: string } | null;
}

const RECENT_DEFAULT_LIMIT = 18;
export const CAMPAIGN_PAGE_SIZE = 12;

/**
 * Removal assignments are operational takedown work, not campaigns a retailer
 * should have to browse alongside installations and campaign evidence.
 */
export function isRetailerCampaign(campaign: CampaignPlacement): boolean {
  return !/^\s*\[?removal\]?\b/i.test(campaign.campaignName);
}

function parseSortDate(value: string | undefined): number {
  if (!value || value === '—') return 0;
  const parsed = new Date(value.length === 10 ? `${value}T12:00:00` : value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

export function resolveCampaignsWithPhases(
  campaigns: CampaignPlacement[],
  asOf: Date,
): CampaignPlacement[] {
  return campaigns.map((campaign) => ({
    ...campaign,
    phase: resolveCampaignPhaseAtDate(campaignPhaseInputFromPlacement(campaign), asOf),
  }));
}

export function filterCampaignsForBrowse(
  campaigns: CampaignPlacement[],
  filter: CampaignBrowseFilter,
  asOf: Date,
): CampaignPlacement[] {
  const resolved = resolveCampaignsWithPhases(campaigns, asOf)
    .filter(isRetailerCampaign);

  switch (filter) {
    case 'active':
      return resolved.filter((campaign) => campaign.phase === 'current');
    case 'previous':
      return resolved.filter((campaign) => campaign.phase === 'previous');
    case 'planned':
      return resolved.filter((campaign) => campaign.phase === 'planned');
    case 'all':
      return resolved;
    case 'recent':
    default:
      return [...resolved]
        .sort((a, b) => {
          const dateDiff = parseSortDate(b.latestImageDate ?? b.installationDate)
            - parseSortDate(a.latestImageDate ?? a.installationDate);
          if (dateDiff !== 0) return dateDiff;
          return b.campaignName.localeCompare(a.campaignName);
        })
        .slice(0, RECENT_DEFAULT_LIMIT);
  }
}

export function sortCampaignsForDisplay(campaigns: CampaignPlacement[]): CampaignPlacement[] {
  return [...campaigns].sort((a, b) => {
    const dateDiff = parseSortDate(b.latestImageDate ?? b.installationDate)
      - parseSortDate(a.latestImageDate ?? a.installationDate);
    if (dateDiff !== 0) return dateDiff;
    const locA = a.instoreLocationName ?? '';
    const locB = b.instoreLocationName ?? '';
    return locA.localeCompare(locB) || a.campaignName.localeCompare(b.campaignName);
  });
}

function locationKey(campaign: CampaignPlacement): string {
  if (campaign.instoreLocationId != null) return `loc:${campaign.instoreLocationId}`;
  if (campaign.instoreLocationName) return `name:${campaign.instoreLocationName.toLowerCase()}`;
  return 'unknown';
}

function locationLabel(campaign: CampaignPlacement): string {
  return campaign.instoreLocationName?.trim() || 'Location not specified';
}

export function getCampaignMapFocus(
  campaign: CampaignPlacement,
  profile: StoreProfileDetail,
): { kind: SpatialEntity['kind']; id: string } | null {
  if (campaign.floorplanZoneId) {
    return { kind: 'zone', id: campaign.floorplanZoneId };
  }
  const entities = buildSpatialEntities(profile);
  const spatial = resolveCampaignSpatialEntity(
    campaign,
    entities,
    profile.floorplanZones,
    profile.aisles,
  );
  if (!spatial) return null;
  return { kind: spatial.kind, id: spatial.id };
}

export function enrichCampaign(
  campaign: CampaignPlacement,
  profile: StoreProfileDetail,
): EnrichedCampaign {
  const locLabel = locationLabel(campaign);
  const instoreLocationId = campaign.instoreLocationId;
  const evidenceImages = instoreLocationId != null
    ? getInstallImagesForInstoreLocation(
      profile.images,
      instoreLocationId,
      campaign.floorplanZoneId ?? null,
      [campaign],
    )
    : profile.images.filter((image) => {
      if (image.hidden) return false;
      if (campaign.campaignTaskId != null && image.assignmentId === campaign.campaignTaskId) return true;
      if (campaign.campaignId && image.campaignId != null && String(image.campaignId) === campaign.campaignId) {
        return image.caption.toLowerCase().includes(campaign.campaignName.toLowerCase());
      }
      return false;
    });

  const galleryUrls = evidenceImages.map((image) => resolveEvidenceImageUrl(image)).filter(Boolean);
  const postingUrls = campaign.installationImageUrls.filter(Boolean);
  const primaryImageUrl = galleryUrls[0] ?? postingUrls[0] ?? null;
  const mapFocus = getCampaignMapFocus(campaign, profile);

  return {
    ...campaign,
    evidenceImages: evidenceImages.map((image) => ({
      ...image,
      url: resolveEvidenceImageUrl(image),
    })),
    primaryImageUrl,
    locationLabel: locLabel,
    onMap: mapFocus != null,
    mapFocus,
  };
}

export function buildCampaignCategoryGroups(
  campaigns: CampaignPlacement[],
  profile: StoreProfileDetail,
): CampaignCategoryGroup[] {
  const groups = new Map<string, CampaignPlacement[]>();

  for (const campaign of campaigns) {
    const key = locationKey(campaign);
    const list = groups.get(key) ?? [];
    list.push(campaign);
    groups.set(key, list);
  }

  return [...groups.entries()]
    .map(([key, groupCampaigns]) => {
      const sample = groupCampaigns[0]!;
      const enriched = enrichCampaign(sample, profile);
      return {
        key,
        locationName: locationLabel(sample),
        instoreLocationId: sample.instoreLocationId ?? null,
        floorplanZoneId: sample.floorplanZoneId ?? null,
        onMap: enriched.onMap,
        campaigns: sortCampaignsForDisplay(groupCampaigns),
        previewImageUrl: enriched.primaryImageUrl,
      };
    })
    .sort((a, b) => b.campaigns.length - a.campaigns.length || a.locationName.localeCompare(b.locationName));
}

export function countCampaignsByPhase(
  campaigns: CampaignPlacement[],
  asOf: Date,
): Record<CampaignPhase, number> {
  const resolved = resolveCampaignsWithPhases(campaigns, asOf)
    .filter(isRetailerCampaign);
  return {
    current: resolved.filter((campaign) => campaign.phase === 'current').length,
    previous: resolved.filter((campaign) => campaign.phase === 'previous').length,
    planned: resolved.filter((campaign) => campaign.phase === 'planned').length,
  };
}

export function campaignStatusLabel(campaign: CampaignPlacement): string {
  return phaseLabel(campaign.phase);
}

export { deriveCampaignPhase, phaseLabel };
